/**
 * useInlineGroup — one entity, several fields, one commit.
 *
 * THE GROUP IS THE COMMIT UNIT, not the field. A registration is a number, a date, an owner and a
 * certificate; a witness is a phone and an address. Saving them one at a time is four writes, four
 * chances to leave the row half-changed, and four bars asking the same question — so the whole
 * group goes dirty together, refuses together and dates itself together.
 *
 * IT IS THE `AgreementsArea` PARTIES BLOCK, LIFTED. That block already wires a reducer, a sync
 * effect, a mutation, the changed-field list and the bar by hand; every group after it would have
 * copied the same forty lines, and the copies are where the "drafts survive a refetch" guarantee
 * quietly stops being true. The rules stay in `inlineEditReducer` where they are provable; this
 * hook is only the wiring around them.
 *
 * TWO THINGS THE CALLER OWNS AND MUST GET RIGHT:
 *  - `server` has to be MEMOISED. It is the sync effect's dependency; a fresh object every render
 *    syncs on every render, and a sync while the block is clean overwrites nothing but still
 *    churns. `useMemo` on the fields it is built from.
 *  - `subject` names WHICH entity the group is about (`witness:41`). Supply it wherever the row
 *    can be repointed, or a draft typed for one person follows the picker onto the next.
 */

import { useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useCallback, useEffect, useMemo, useReducer } from 'react';

import {
    changedFields,
    commitHint,
    initialInlineState,
    inlineEditReducer,
    isDirty,
    rejectedSummary,
    type FieldValues,
    type InlineState,
} from './inlineEditReducer';

/** What a quiet field needs to be wired into a group, in one spread. */
export interface InlineGroupField {
    value: string;
    onValueChange: (value: string) => void;
    dirty: boolean;
    error: string | null;
    /** A group in flight does not take edits — the write already left with these values. */
    readOnly: boolean;
}

export interface InlineGroup {
    state: InlineState;
    /** What the inputs are showing. */
    draft: FieldValues;
    dirty: boolean;
    /** The fields that differ from the server, for a hazard dialog's before/after. */
    changed: string[];
    saving: boolean;
    /**
     * The refusal that has nowhere better to go, verbatim.
     *
     * NULL ONCE THE REFUSAL HAS A FIELD. §A4 puts the server's sentence directly under the input
     * it is about; printing the same sentence again at the foot of the group reads as the block
     * having been refused twice. `state.error` still holds it either way — this is only what the
     * group's own line draws from, and it draws it exactly when nothing else will.
     */
    error: string | null;
    /** The eyebrow's `2 fields rejected`, or undefined below two. */
    rejected: string | undefined;
    /** The commit bar's own line: `3 fields commit together`. */
    hint: string;
    /** `Aug 3, 2026 · 14:22` once this group has committed in this session. */
    savedAt: string | null;
    /** Everything a quiet field needs: `<QuietField {...group.field('phone')} … />`. */
    field: (name: string) => InlineGroupField;
    set: (name: string, value: string) => void;
    commit: () => void;
    discard: () => void;
}

export interface UseInlineGroupArgs {
    /** The values as the server last reported them. MEMOISE THIS. */
    server: FieldValues;
    /** Which entity the values belong to; changing it drops the draft (reducer rule 5). */
    subject?: string | null;
    /**
     * The write. Gets the draft, and may refuse with the server's own sentence.
     *
     * `touched` NAMES THE FIELDS THE ADMIN ACTUALLY TYPED INTO (reducer rule 6), for the writes
     * that must not treat an empty field as a statement that it is empty. Most groups ignore it:
     * their fields all arrive from one read, so the draft is never seeded from a read still in
     * flight. A group fed by two reads has to ask — see `AgreementsArea`'s parties block.
     */
    save: (draft: FieldValues, touched: string[]) => Promise<unknown>;
    /** Invalidate the reads this write moved. Runs after the group has gone clean. */
    onSaved?: (draft: FieldValues) => void;
    /** Field-scoped refusals, where the write can attribute them. */
    fieldErrorsFor?: (error: Error) => Record<string, string> | undefined;
}

export function useInlineGroup({
    server,
    subject = null,
    save,
    onSaved,
    fieldErrorsFor,
}: UseInlineGroupArgs): InlineGroup {
    const [state, dispatch] = useReducer(inlineEditReducer, server, (values) =>
        initialInlineState(values, subject),
    );

    useEffect(() => {
        dispatch({ type: 'sync', server, subject });
    }, [server, subject]);

    /**
     * THE DRAFT TRAVELS WITH THE WRITE, AND SO DOES WHAT WAS TYPED INTO IT. Taking either off
     * `state` inside `onSuccess` instead would adopt whatever the fields hold when the answer
     * lands — and a keystroke that arrived while the write was in flight would be recorded as the
     * server's truth without ever being sent.
     */
    const commitMutation = useMutation({
        mutationFn: ({ draft, touched }: { draft: FieldValues; touched: string[] }) =>
            save(draft, touched),
        onSuccess: (_data, { draft }) => {
            dispatch({
                type: 'committed',
                server: draft,
                at: format(new Date(), 'MMM d, yyyy · HH:mm'),
            });
            onSaved?.(draft);
        },
        // VERBATIM, AND THE DRAFT STAYS. Whatever the write raised — PostgREST's sentence, a
        // guard's own — is what the admin reads, where they typed.
        onError: (error: Error) =>
            dispatch({
                type: 'failed',
                message: error.message || 'The save was refused.',
                fieldErrors: fieldErrorsFor?.(error),
            }),
    });

    const dirty = isDirty(state);
    const changed = useMemo(() => changedFields(state), [state]);

    const set = useCallback((name: string, value: string) => {
        dispatch({ type: 'edit', field: name, value });
    }, []);

    const discard = useCallback(() => dispatch({ type: 'cancel' }), []);

    const commit = () => {
        dispatch({ type: 'commit' });
        commitMutation.mutate({ draft: state.draft, touched: state.touched });
    };

    const saving = state.status === 'saving';

    const field = useCallback(
        (name: string): InlineGroupField => ({
            value: state.draft[name] ?? '',
            onValueChange: (value: string) => set(name, value),
            dirty: (state.draft[name] ?? '') !== (state.server[name] ?? ''),
            error: state.fieldErrors[name] ?? null,
            readOnly: saving,
        }),
        [state.draft, state.server, state.fieldErrors, set, saving],
    );

    return {
        state,
        draft: state.draft,
        dirty,
        changed,
        saving,
        error: Object.keys(state.fieldErrors).length > 0 ? null : state.error,
        rejected: rejectedSummary(state),
        hint: commitHint(state),
        savedAt: state.savedAt,
        field,
        set,
        commit,
        discard,
    };
}
